import React, { ReactElement, useEffect, useRef } from 'react';
import { BackHandler, Linking, StyleSheet } from 'react-native';
import WebView from 'react-native-webview';

import { CloseButton, CloseText, Container } from './styled';

// Sits above the login/onboarding content. Keep in sync with any other
// full-screen overlay rendered alongside it (e.g. LoadingOverlay).
const OVERLAY_LAYER = 999;

type Props = {
  visible: boolean;
  url: string;
  callbackUrl: string;
  onSuccess: (path: string) => void;
  onCancel: () => void;
  onError: () => void;
};

const NHSWebViewModal = ({
  visible,
  url,
  callbackUrl,
  onSuccess,
  onCancel,
  onError,
}: Props): ReactElement | null => {
  const callbackScheme = callbackUrl.slice(0, callbackUrl.indexOf('://') + 3);
  const webViewRef = useRef<WebView>(null);
  const canGoBackRef = useRef(false);

  // Android: when the WebView navigates to a custom scheme, the OS may dispatch
  // a system Intent instead of calling onShouldStartLoadWithRequest. Handle both.
  useEffect(() => {
    if (!visible) {
      return;
    }
    const subscription = Linking.addEventListener(
      'url',
      ({ url: incomingUrl }) => {
        if (incomingUrl.startsWith(callbackUrl)) {
          onSuccess(incomingUrl.replace(callbackScheme, ''));
        }
      }
    );
    return () => subscription.remove();
  }, [visible, callbackUrl, callbackScheme, onSuccess]);

  // Now that this is a plain overlay instead of an RN <Modal>, the Android
  // hardware back button no longer triggers onRequestClose. Wire it up manually
  // so pressing back navigates back in the WebView flow when possible, or
  // cancels/closes, matching the previous Modal behavior.
  useEffect(() => {
    if (!visible) {
      return;
    }
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (canGoBackRef.current) {
        webViewRef.current?.goBack();
      } else {
        onCancel();
      }
      return true;
    });
    return () => sub.remove();
  }, [visible, onCancel]);

  // Full-screen overlay (not an RN <Modal>) so the WebView lives in MainActivity's
  // window and resizes with the keyboard — the <Modal> window does not on Android 15.
  return visible ? (
    // Positioning is driven by StyleSheet.absoluteFill; Container's `flex: 1`
    // is inert here and only its background/safe-area insets apply.
    <Container
      style={[
        StyleSheet.absoluteFill,
        { zIndex: OVERLAY_LAYER, elevation: OVERLAY_LAYER },
      ]}
    >
      <CloseButton
        onPress={onCancel}
        accessibilityRole="button"
        accessibilityLabel="Close"
      >
        <CloseText>✕</CloseText>
      </CloseButton>
      <WebView
        ref={webViewRef}
        style={{ flex: 1 }}
        source={{ uri: url }}
        onError={onError}
        onNavigationStateChange={navState => {
          canGoBackRef.current = navState.canGoBack;
        }}
        onShouldStartLoadWithRequest={request => {
          if (request.url.startsWith(callbackUrl)) {
            onSuccess(request.url.replace(callbackScheme, ''));
            return false;
          }
          return true;
        }}
      />
    </Container>
  ) : null;
};

export { NHSWebViewModal };
